import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type BemTipo = 'MOVEL' | 'IMOVEL' | 'VEICULO';

export interface Bem {
  id: string;
  workspaceId: string;
  acordoId: string | null;
  descricao: string;
  tipo: BemTipo;
  valor: string;
  dataAquisicao: string;
  estado: string | null;
  localizacao: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface BensListResult {
  items: Bem[];
  total: number;
  limit: number;
  offset: number;
}

export interface BensAggregate {
  count: number;
  total: string;
  buckets: {
    MOVEL: { count: number; total: string };
    IMOVEL: { count: number; total: string };
    VEICULO: { count: number; total: string };
  };
}

export interface BemFilters {
  tipo?: BemTipo;
  search?: string;
  dataInicio?: string;
  dataFim?: string;
  limit?: number;
  offset?: number;
}

export interface CreateBemInput {
  descricao: string;
  tipo: BemTipo;
  valor: string;
  dataAquisicao: string;
  estado?: string;
  localizacao?: string;
  acordoId?: string;
}

function basePath(workspaceId: string): string {
  return `/v1/workspaces/${workspaceId}/bens`;
}

function buildQuery(filters: BemFilters): string {
  const q = new URLSearchParams();
  if (filters.tipo) q.set('tipo', filters.tipo);
  if (filters.search) q.set('search', filters.search);
  if (filters.dataInicio) q.set('dataInicio', filters.dataInicio);
  if (filters.dataFim) q.set('dataFim', filters.dataFim);
  if (filters.limit != null) q.set('limit', String(filters.limit));
  if (filters.offset != null) q.set('offset', String(filters.offset));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function useBens(workspaceId: string | null | undefined, filters: BemFilters) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['bens', workspaceId, filters],
    queryFn: () => api.get<BensListResult>(`${basePath(workspaceId!)}/${buildQuery(filters)}`),
  });
}

export function useBensAggregate(workspaceId: string | null | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['bens', workspaceId, 'aggregate'],
    queryFn: () => api.get<BensAggregate>(`${basePath(workspaceId!)}/aggregate`),
  });
}

export function useCreateBem(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBemInput) => api.post<Bem>(`${basePath(workspaceId!)}/`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bens', workspaceId] });
    },
  });
}

export function useDeleteBem(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`${basePath(workspaceId!)}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bens', workspaceId] });
    },
  });
}
